import { LoginPage } from "@app/pages/login/LoginPage";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "@app/hooks/useAuth";
import { MainPage } from "./MainPage";
import { rootPages } from "@app/const/route.const";
import { AppDialogs } from "./AppDialogs";
import { AUTH_INFO_KEY } from "@app/redux/authSlice";
import { AppToast } from "./AppToast";
import { ReactNode } from "react";
import { Box, CssBaseline, GlobalStyles, styled } from "@mui/material";
import { globalStyles } from "@app/theme/globalStyles";
import { PaymentChecker, PaymentPage } from "@app/pages/payment/PaymentPage";
import { AcceptInvitationPage } from "@app/pages/acceptInvitation/AcceptInvitationPage";
import { PaymentCompletePage } from "@app/pages/payment/PaymentCompletePage";
import { ResetPasswordPage } from "@app/pages/resetPassword/ResetPasswordPage";
import { IntercomButton } from "@app/components/IntercomButton";

const RequireAuth = (props: { children?: ReactNode }) => {
  const auth = useAuth();
  const location = useLocation();

  // Check if there is a query param named "auth" in the URL
  const [searchParams] = useSearchParams();
  const auth_string = searchParams.get("auth");
  if (auth_string) {
    searchParams.delete("auth");
    sessionStorage.setItem(AUTH_INFO_KEY, auth_string);
    localStorage.removeItem(AUTH_INFO_KEY);
    // TODO Reload the page
  }

  if (!auth.is_complete) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return (
      <Navigate to={"/" + rootPages.login} state={{ from: location }} replace />
    );
  }

  return <>{props.children}</>;
};

const AppRoot = styled(Box)({
  height: "100%",
  width: "100%",
  display: "flex",
  alignItems: "stretch",
});

export const App = () => {
  return (
    <AppRoot>
      <CssBaseline />
      <GlobalStyles styles={globalStyles} />
      <Routes>
        <Route path={rootPages.login} element={<LoginPage />} />
        <Route
          path={rootPages.startFreeTrial}
          element={<LoginPage showTrialPage />}
        />
        <Route
          path={rootPages.acceptInvitationPath}
          element={<AcceptInvitationPage />}
        />
        <Route
          path={rootPages.resetPasswordPath}
          element={<ResetPasswordPage />}
        />
        <Route
          path={rootPages.payment}
          element={
            <RequireAuth>
              <PaymentPage />
            </RequireAuth>
          }
        />
        <Route
          path={rootPages.paymentComplete}
          element={
            <RequireAuth>
              <PaymentCompletePage />
            </RequireAuth>
          }
        />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <MainPage />
              <PaymentChecker />
              <IntercomButton />
            </RequireAuth>
          }
        />
      </Routes>
      <AppDialogs />
      <AppToast />
    </AppRoot>
  );
};
